import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { Hangar } from "../../types";
import { MetricAwareTextField } from "../../widgets/MetricAwareTextField/MetricAwareTextField";
import { Aircraft } from "../../types";
import { AutocompleteAircraft } from "../Settings/HangarSettings/AutocompleteAircraft";
import { range } from "../../utils/lodash-replacements";

type Props = {
  localHangar: Hangar;
  setLocalHangar: (h: Hangar) => void;
  widthIncrement: number;
  setWidthIncrement: (w: number) => void;
  depthIncrement: number;
  setDepthIncrement: (d: number) => void;
  selectedAircraft: Aircraft | null;
  setSelectedAircraft: (a: Aircraft) => void;
  nAircraft: number;
  setNAircraft: (n: number) => void;
  aircrafts: Aircraft[];
  setAircrafts: (a: Aircraft[]) => void;
};

export const OptimizationInputsForm: React.FC<Props> = ({
  localHangar,
  setLocalHangar,
  depthIncrement,
  setDepthIncrement,
  widthIncrement,
  setWidthIncrement,
  selectedAircraft,
  setSelectedAircraft,
  nAircraft,
  setNAircraft,
  aircrafts,
  setAircrafts,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";

  // Compute the sizes to be tested
  const widths = React.useMemo(
    () =>
      range(
        localHangar.width - widthIncrement,
        localHangar.width + widthIncrement,
        widthIncrement / 2
      ),
    [localHangar.width, widthIncrement]
  );
  const depths = React.useMemo(
    () =>
      range(
        localHangar.depth - depthIncrement,
        localHangar.depth + depthIncrement,
        depthIncrement / 2
      ),
    [localHangar.depth, depthIncrement]
  );

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column">
        <Typography variant="subtitle1" fontWeight="bold">
          {t("screens.hangarBuilderOptimizer.optimizationInputsForm.sections.initialDimensions")}
        </Typography>
        <Stack direction="row" spacing={2} p={2}>
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 50, max: 500 } }}
            size="small"
            label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.width", { unit })}
            onChange={(value: number) =>
              setLocalHangar({ ...localHangar, width: value })
            }
            value={localHangar.width}
          />
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 50, max: 500 } }}
            size="small"
            label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.depth", { unit })}
            onChange={(value: number) =>
              setLocalHangar({ ...localHangar, depth: value })
            }
            value={localHangar.depth}
          />
        </Stack>
      </Stack>
      <Stack direction="column">
        <Typography variant="subtitle1" fontWeight="bold">
          {t("screens.hangarBuilderOptimizer.optimizationInputsForm.sections.variations")}
        </Typography>
        <Stack direction="row" spacing={2} p={2}>
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            disabled={activeFBO.subscription === "standard"}
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 5, max: 100 } }}
            size="small"
            label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.widthVariation", { unit })}
            onChange={setWidthIncrement}
            value={widthIncrement}
          />
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            disabled={activeFBO.subscription === "standard"}
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 5, max: 100 } }}
            size="small"
            label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.depthVariation", { unit })}
            onChange={setDepthIncrement}
            value={depthIncrement}
          />
        </Stack>
      </Stack>
      <Stack direction="column">
        <Typography variant="subtitle1" fontWeight="bold">
          {t("screens.hangarBuilderOptimizer.optimizationInputsForm.sections.sizesToTest")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1 }}>
          {widths.length * depths.length}{" "}
          {t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.configurations")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: 2 }}>
          {widths.map((w) =>
            depths.map((d) => (
              <Chip
                key={`${w}x${d}`}
                label={`${w}${unit} x ${d}${unit}`}
                size="small"
                variant={
                  w === localHangar.width && d === localHangar.depth
                    ? "filled"
                    : "outlined"
                }
                color={
                  w === localHangar.width && d === localHangar.depth
                    ? "primary"
                    : "default"
                }
              />
            ))
          )}
        </Box>
      </Stack>
      <Stack direction="column" width={600}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t("screens.hangarBuilderOptimizer.optimizationInputsForm.sections.aircraftMix")}
        </Typography>
        <Stack direction="row" spacing={2} p={2} alignItems="flex-start">
          <Box width={300} flexShrink={0}>
            <AutocompleteAircraft
              label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.searchAircraft")}
              onSelect={setSelectedAircraft}
            />
          </Box>
          <TextField
            label={t("screens.hangarBuilderOptimizer.optimizationInputsForm.labels.numberAircraft")}
            type="number"
            variant="outlined"
            size="small"
            inputProps={{ min: 1, max: 20 }}
            value={nAircraft}
            onChange={(e) => {
              setNAircraft(parseInt(e.target.value) || 1);
            }}
            sx={{
              width: 110,
            }}
          />
          <IconButton
            color="primary"
            onClick={() => {
              if (selectedAircraft && nAircraft > 0) {
                const newAircrafts = [...aircrafts];
                for (let i = 0; i < nAircraft; i++) {
                  newAircrafts.push(selectedAircraft);
                }
                setAircrafts(newAircrafts);
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("screens.hangarBuilderOptimizer.optimizationInputsForm.tableHeaders.make")}</TableCell>
              <TableCell>{t("screens.hangarBuilderOptimizer.optimizationInputsForm.tableHeaders.model")}</TableCell>
              <TableCell>{t("screens.hangarBuilderOptimizer.optimizationInputsForm.tableHeaders.wingspan", { unit })}</TableCell>
              <TableCell>{t("screens.hangarBuilderOptimizer.optimizationInputsForm.tableHeaders.length", { unit })}</TableCell>
              <TableCell>{t("screens.hangarBuilderOptimizer.optimizationInputsForm.tableHeaders.tailHeight", { unit })}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aircrafts.map((aircraft, index) => (
              <TableRow key={index}>
                <TableCell>{aircraft.make}</TableCell>
                <TableCell>{aircraft.model}</TableCell>
                <TableCell>{aircraft.wingspan}</TableCell>
                <TableCell>{aircraft.length}</TableCell>
                <TableCell>{aircraft.tail_height}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      const newAircrafts = [...aircrafts];
                      newAircrafts.splice(index, 1);
                      setAircrafts(newAircrafts);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Stack>
  );
};
